import type { ReactElement } from 'react';
import { useAuth } from './auth/AuthContext';
import { navigate, useHashRoute } from './hooks';
import { AdminPage } from './pages/AdminPage';
import { AttentionPage } from './pages/AttentionPage';
import { FinancePage } from './pages/FinancePage';
import { InboxPage } from './pages/InboxPage';
import { LoginPage } from './pages/LoginPage';
import { MailAccountsPage } from './pages/MailAccountsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { RequestCardPage } from './pages/RequestCardPage';
import { RequestsPage } from './pages/RequestsPage';
import { TechQueuePage } from './pages/TechQueuePage';
// === WT5 recon ===
import { ReconciliationPage } from './pages/ReconciliationPage';

const NAV = [
  { path: '/requests', title: 'Заявки' },
  { path: '/inbox', title: 'Входящие', roles: ['manager', 'supervisor', 'admin'] },
  { path: '/tech-queue', title: 'Очередь техотдела' },
  { path: '/finance', title: 'Финансы' },
  // === WT5 recon ===
  { path: '/recon', title: 'Сверки поставщиков', roles: ['accountant', 'specialist', 'dispatcher', 'supervisor', 'admin'] },
  { path: '/attention', title: 'Требуют внимания' },
  { path: '/notifications', title: 'Уведомления' },
  { path: '/mail-accounts', title: 'Ящики' },
  { path: '/admin', title: 'Администрирование' },
];

function displayName(u: { username: string; first_name?: string; last_name?: string } | null): string {
  if (!u) return '';
  const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  return full || u.username;
}

export default function App() {
  const { user, ready, loading, logout } = useAuth();
  const route = useHashRoute();

  if (!ready) {
    return <div className="splash">Загрузка REGWAY CRM…</div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  let page: ReactElement;
  const cardMatch = route.match(/^\/requests\/([0-9a-fA-F-]{36})/);
  if (cardMatch) {
    page = <RequestCardPage requestId={cardMatch[1]} />;
  } else if (route.startsWith('/inbox')) {
    page = <InboxPage />;
  } else if (route.startsWith('/tech-queue')) {
    page = <TechQueuePage />;
  } else if (route.startsWith('/finance')) {
    page = <FinancePage />;
  // === WT5 recon ===
  } else if (route.startsWith('/recon')) {
    page = <ReconciliationPage />;
  } else if (route.startsWith('/attention')) {
    page = <AttentionPage />;
  } else if (route.startsWith('/notifications')) {
    page = <NotificationsPage />;
  } else if (route.startsWith('/mail-accounts')) {
    page = <MailAccountsPage />;
  } else if (route.startsWith('/admin')) {
    page = <AdminPage />;
  } else {
    page = <RequestsPage />;
  }

  const isAdmin = Boolean(user.is_staff || user.is_superuser);
  const userRoles = new Set(user.roles ?? []);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">REGWAY CRM</div>
        <nav>
          {NAV.map((item) => {
            if (item.path === '/admin' && !isAdmin) return null;
            if ('roles' in item && item.roles && !isAdmin
                && !item.roles.some((r) => userRoles.has(r))) return null;
            const active = route.startsWith(item.path);
            return (
              <a key={item.path}
                 href={`#${item.path}`}
                 className={active ? 'active' : ''}>
                {item.title}
              </a>
            );
          })}
        </nav>
        <div className="sidebar-footer">v0.1 · Stage 3</div>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="muted">
            <a href="#/requests" onClick={(e) => { e.preventDefault(); navigate('/requests'); }}>
              Заявки
            </a>
          </div>
          <div className="user-info">
            <span>{displayName(user)}{user.is_staff ? ' · администратор' : ''}</span>
            <button className="btn small" onClick={() => void logout()} disabled={loading}>
              Выйти
            </button>
          </div>
        </header>
        <main className="content">{page}</main>
      </div>
    </div>
  );
}
